/* eslint-disable no-new */
import { initCarousel } from '@/braun/Controllers/BraunCarouselController';
import PdpZoomGalleryLoader from '@/pdp-braun/Controllers/PdpZoomGalleryLoader';
import { pushGtmProductZoom } from '@/utils/Controllers/GTM/Operations/ProductZoom';
import {
  pushGtmVideoComplete,
  pushGtmVideoPause,
  pushGtmVideoPlay,
} from '@/utils/Controllers/GTM/Operations/Video';
import LoadJS from '@/utils/LoadJS';
import _debounce from 'lodash/debounce';
import _keys from 'lodash/keys';

const mSize = 1023;
const RESIZE_EVENT = 'PDP_GALLERY_RESIZE';

export class PdpGalleryControllerClass {
  constructor({ selector = '#js-bra-prodGallery', slideNum = 0 } = {}) {
    this.productGallery = document.querySelector(selector);
    this.productGallerySlider = this.productGallery.querySelector(
      '.swiper-container',
    );
    this.slideNum = parseInt(slideNum, 10);
    this.setup();
    this.setListeners();
    this.loadCarousel();
  }

  setup() {
    this.videoThumbnails = Array.from(
      this.productGallerySlider.querySelectorAll(
        '.bra-prodGallery__slideIframe',
      ),
    );
    this.firstThumb = this.productGallerySlider.getElementsByClassName(
      'swiper-slide',
    )[this.slideNum];
    this.imagePreview = this.productGallery.querySelector(
      '.bra-prodGallery__previewImage',
    );
    this.iframePreview = this.productGallery.querySelector(
      '.bra-prodGallery__previewIframe',
    );
    this.lastClickedThumb = null;
    this.lastWinWidth = window.innerWidth;
    this.playersYT = [];
  }

  setListeners() {
    this.resizeHandler = this.resizeHandler.bind(this);
    this.handleThumbnailsIframe = this.handleThumbnailsIframe.bind(this);
    this.initializeVideoController = this.initializeVideoController.bind(this);
    this.manageVideoState = this.manageVideoState.bind(this);
    this.pauseAllVideos = this.pauseAllVideos.bind(this);
    this.imagePreview.addEventListener('click', () => {
      this.openZoom();
    });
    this.imagePreview.addEventListener('keydown', e => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code === 'Enter' || e.code === 'Space') {
        this.openZoom();
      }
    });
    DLG.EVE.on(RESIZE_EVENT, this.resizeHandler);
  }

  openZoom() {
    PdpZoomGalleryLoader.load(this.imagePreview.dataset.slideNum);
    // gtm
    pushGtmProductZoom();
  }

  resizeHandler() {
    if (this.lastWinWidth <= mSize && window.innerWidth > mSize) {
      this.handleGalleryPreview(this.lastClickedThumb);
      this.pauseAllVideos();
    }

    this.lastWinWidth = window.innerWidth;
  }

  async loadCarousel() {
    const self = this;
    this.swiperSlider = await initCarousel(
      this.productGallerySlider,
      {
        slidesPerViewXl: 'auto',
        slidesPerViewL: 'auto',
        slidesPerViewM: 'auto',
        slidesPerViewXs: 'auto',

        directionXl: 'vertical',
        directionL: 'vertical',
        directionM: 'horizontal',
        directionXs: 'horizontal',
        hideScrollbar: false,
      },
      {
        preloadImages: false,
        resistance: false,
        on: {
          init() {
            self.handleThumbnailsIframe();
            if (window.innerWidth > mSize) {
              self.handleGalleryPreview(self.firstThumb);
            }
          },
          click() {
            if (self.swiperSlider.clickedSlide) {
              self.handleGalleryPreview(self.swiperSlider.clickedSlide);
            }
          },
          slideChange() {
            self.pauseAllVideos();
            const overlay = document.querySelector(
              '.bra-prodGallery__slideIframeOverlay',
            );
            if (overlay && window.innerWidth < mSize) {
              overlay.style.display = 'flex';
            }
          },
        },
      },
    );

    this.swiperSlider.el
      .querySelectorAll('.swiper-slide')
      .forEach((slide, index) => {
        slide.addEventListener('focus', () => {
          this.swiperSlider.slideTo(index);
        });
        slide.addEventListener('keydown', e => {
          if (e.code === 'Enter' || e.code === 'Space') {
            e.preventDefault();
            e.stopPropagation();
            slide.click();
          }
        });
      });
  }

  handleThumbnailsIframe() {
    this.videoThumbnails.forEach(videoThumbnail => {
      const video = videoThumbnail.querySelector('.bra-prodGallery__iframe');
      const videoID = video.dataset.videoId;
      this.playersYT[videoID] = new YT.Player(video, {
        videoId: videoID,
        playerVars: {
          origin: window.location.origin,
        },
        events: {
          onReady: this.initializeVideoController,
          onStateChange: this.manageVideoState,
        },
      });
    });
  }

  initializeVideoController(event) {
    const videoID = event.target.getVideoData().video_id;
    const videoContainer = event.target.getIframe().parentElement;
    const playBtn = videoContainer.querySelector('.bra-playBtn');
    const overlay = document.querySelector(
      '.bra-prodGallery__slideIframeOverlay',
    );
    overlay.style.display = 'flex';
    playBtn.addEventListener('click', () => {
      if (typeof this.playersYT[videoID].playVideo === 'function') {
        this.playersYT[videoID].playVideo();
        videoContainer.classList.add(
          'bra-prodGallery__slideIframe--hidePoster',
        );
        if (window.innerWidth < mSize) {
          overlay.style.display = 'none';
        }
      }
    });
  }

  manageVideoState(event) {
    const iframe = event.target.getIframe();
    const gtmInfo = {
      videoTitle: iframe.dataset.videoTitle,
      videoId: iframe.dataset.videoId,
      videoUrl: iframe.dataset.videoUrl,
      howTo: '',
    };
    switch (event.data) {
      case YT.PlayerState.PLAYING:
        pushGtmVideoPlay(gtmInfo);
        break;
      case YT.PlayerState.PAUSED:
        pushGtmVideoPause(gtmInfo);
        break;
      case YT.PlayerState.ENDED:
        event.target.stopVideo();
        this.removeHidePoster();
        pushGtmVideoComplete(gtmInfo);
        break;
      default:
        break;
    }
  }

  removeHidePoster() {
    this.videoThumbnails.forEach(videoThumbnail => {
      videoThumbnail.classList.remove(
        'bra-prodGallery__slideIframe--hidePoster',
      );
    });
  }

  pauseAllVideos() {
    _keys(this.playersYT).forEach(videoID => {
      if (typeof this.playersYT[videoID].playVideo === 'function') {
        this.playersYT[videoID].pauseVideo();
        this.removeHidePoster();
      }
    });
  }

  handleGalleryPreview(selectedThumb) {
    if (window.innerWidth > mSize) {
      const isImageSlide = selectedThumb.classList.contains(
        'bra-prodGallery__slide--image',
      );
      const isIframeSlide = selectedThumb.classList.contains(
        'bra-prodGallery__slide--iframe',
      );
      const currentSlide = this.productGallery.querySelector(
        '.bra-prodGallery__previewVisible',
      );
      const thumbChilds = selectedThumb.cloneNode(true);
      if (!currentSlide || this.lastClickedThumb !== selectedThumb) {
        if (currentSlide) {
          currentSlide.classList.remove('bra-prodGallery__previewVisible');

          if (this.iframePreview.childNodes[0]) {
            this.iframePreview.removeChild(this.iframePreview.childNodes[0]);
          }
        }
        if (isImageSlide) {
          const { slideNum } = selectedThumb.dataset;
          const dataPreviewL = selectedThumb
            .querySelector('.dln-image__sourceThumbnail')
            .getAttribute('data-preview-image-l');
          const dataPreviewM = selectedThumb
            .querySelector('.dln-image__sourceThumbnail')
            .getAttribute('data-preview-image-m');
          this.imagePreview.dataset.slideNum = slideNum;
          const imgTag = this.imagePreview.getElementsByTagName('img')[0];
          if (parseInt(slideNum, 10) === 0) {
            imgTag.removeAttribute('data-src');
            imgTag.setAttribute('fetchpriority', 'high');
            imgTag.removeAttribute('loading');
            imgTag.classList.remove('lazyload');
            imgTag.setAttribute('src', dataPreviewL);
          } else {
            imgTag.removeAttribute('src');
            imgTag.removeAttribute('fetchpriority');
            imgTag.setAttribute('loading', 'lazy');
            imgTag.classList.add('lazyload');
            imgTag.setAttribute('data-src', dataPreviewL);
          }
          this.imagePreview
            .getElementsByClassName('bra-productGallery__previewSource--l')[0]
            .setAttribute('srcset', dataPreviewL);
          this.imagePreview
            .getElementsByClassName('bra-productGallery__previewSource--m')[0]
            .setAttribute('srcset', dataPreviewM);
          this.imagePreview.classList.add('bra-prodGallery__previewVisible');
        }
        if (isIframeSlide) {
          this.iframePreview.appendChild(thumbChilds.children[0].children[0]);
          this.iframePreview.classList.add('bra-prodGallery__previewVisible');
          const videoID = this.iframePreview.firstChild.dataset.videoId;
          const currentTime = this.playersYT[videoID].getCurrentTime();
          this.playersYT[videoID] = new YT.Player(
            this.iframePreview.firstChild,
            {
              videoId: videoID,
              playerVars: {
                origin: window.location.origin,
              },
              events: {
                onStateChange: this.manageVideoState,
                onReady: () => {
                  this.playersYT[videoID].loadVideoById({
                    videoId: videoID,
                    startSeconds: currentTime,
                  });
                  this.playersYT[videoID].playVideo();
                },
              },
            },
          );
        }
      }
    }
    this.lastClickedThumb = selectedThumb;
  }
}

export default () => {
  LoadJS({
    src: 'https://www.youtube.com/iframe_api',
  });
  window.onYouTubeIframeAPIReady = () => {
    new PdpGalleryControllerClass();
    window.addEventListener(
      'resize',
      _debounce(() => DLG.EVE.emit(RESIZE_EVENT), 300),
    );
  };
};
